import { useCallback, useEffect, useRef, useState } from "react";
import { notificationsApi } from "@/services/notifications";

/**
 * Estado del flujo de Web Push del navegador.
 *
 * - `unsupported`: el navegador no soporta service workers / PushManager /
 *   Notification.
 * - `unconfigured`: el backend no tiene VAPID configurado (publicKey null).
 * - `idle`: soportado pero aún sin intentar suscribir.
 * - `subscribing`: registrando SW / pidiendo permiso / suscribiendo.
 * - `subscribed`: suscripción activa y enviada al backend.
 * - `denied`: el usuario rechazó el permiso de notificaciones.
 * - `error`: fallo durante el registro o la suscripción.
 */
export type WebPushStatus =
  | "unsupported"
  | "unconfigured"
  | "idle"
  | "subscribing"
  | "subscribed"
  | "denied"
  | "error";

export interface UseWebPushResult {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
  status: WebPushStatus;
  error: string | null;
  /**
   * (Re)intenta habilitar push: registra el SW, pide permiso si hace falta y
   * crea la suscripción. Idempotente y seguro de llamar varias veces.
   */
  enable: () => Promise<boolean>;
}

const SW_URL = "/sw-push.js";
const SW_SCOPE = "/sw-push/";

function isWebPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Convierte la clave pública VAPID (base64url) al Uint8Array que exige
 * `pushManager.subscribe({ applicationServerKey })`.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Hook de Web Push: detecta soporte, registra el service worker dedicado,
 * obtiene la clave VAPID del backend y crea/envía la suscripción.
 *
 * @param options.autoEnable Si es true y el permiso ya está concedido (o por
 *   defecto, "default"), intenta habilitar push una vez. Respeta que el
 *   navegador exige permiso explícito: si el usuario lo deniega, degrada en
 *   silencio. Nunca pide permiso en bucle.
 */
export function useWebPush(
  options: { autoEnable?: boolean } = {},
): UseWebPushResult {
  const autoEnable = options.autoEnable ?? false;
  const supported = isWebPushSupported();
  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >(supported ? Notification.permission : "unsupported");
  const [status, setStatus] = useState<WebPushStatus>(
    supported ? "idle" : "unsupported",
  );
  const [error, setError] = useState<string | null>(null);
  // Evita reintentos en bucle del auto-enable y suscripciones concurrentes.
  const attemptedRef = useRef(false);
  const inFlightRef = useRef(false);

  const enable = useCallback(async (): Promise<boolean> => {
    if (!supported) {
      setStatus("unsupported");
      return false;
    }
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setError(null);
    setStatus("subscribing");

    try {
      // 1) Clave VAPID. Si no está configurada, no-op silencioso.
      const { publicKey } = await notificationsApi.push.vapidPublicKey();
      if (!publicKey) {
        console.info(
          "[push] VAPID no configurado en backend; push deshabilitado.",
        );
        setStatus("unconfigured");
        return false;
      }

      // 2) Permiso. Solo pedimos si está en "default".
      let currentPermission = Notification.permission;
      if (currentPermission === "default") {
        currentPermission = await Notification.requestPermission();
      }
      setPermission(currentPermission);
      if (currentPermission !== "granted") {
        setStatus(currentPermission === "denied" ? "denied" : "idle");
        return false;
      }

      // 3) Service worker + suscripción.
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            publicKey,
          ) as BufferSource,
        }));

      // 4) Envío al backend (endpoint + claves p256dh/auth).
      const payload: PushSubscriptionJSON = subscription.toJSON();
      const endpoint = payload.endpoint;
      const p256dhKey = payload.keys?.p256dh;
      const authKey = payload.keys?.auth;
      if (!endpoint || !p256dhKey || !authKey) {
        throw new Error(
          "La suscripción push no devolvió las claves esperadas.",
        );
      }
      await notificationsApi.push.subscribe({
        endpoint,
        p256dhKey,
        authKey,
        userAgent: navigator.userAgent,
      });

      setStatus("subscribed");
      return true;
    } catch (caught) {
      setError(
        errorMessage(caught, "No se pudo activar las notificaciones push."),
      );
      setStatus("error");
      return false;
    } finally {
      inFlightRef.current = false;
    }
  }, [supported]);

  // Auto-activación tras login (una sola vez por montaje). Solo intenta si el
  // permiso aún no fue denegado, para no pedir permiso en bucle.
  useEffect(() => {
    if (!autoEnable || !supported || attemptedRef.current) return;
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    attemptedRef.current = true;
    void enable();
  }, [autoEnable, supported, enable]);

  return { supported, permission, status, error, enable };
}
